import React, { createContext, useReducer } from 'react';
import { getUserEmail } from '../../utils/Auth';

export const AdvancedSearchV2Context = createContext();

const SITE_FILTERS = {
  verticals: [
    { label: 'FinTech', value: 'FinTech', checked: true, onClass: 'ft' },
    { label: 'Real Estate', value: 'Real Estate', checked: false, onClass: 're' },
    { label: 'Cloud', value: 'Cloud', checked: false, onClass: 'ci' },
    { label: 'Cybersecurity', value: 'Security', checked: false, onClass: 'cs' },
    { label: 'AI/ML', value: 'AI/ML', checked: false, onClass: 'ai' },
    { label: 'MarTech', value: 'MarTech', checked: false, onClass: 'mt' },
    { label: 'Insurtech', value: 'Insurtech', checked: false, onClass: 'it' },
  ],
  vc_score: [
    { label: 'All', value: '', checked: false, onClass: 'cs' },
    { label: '1+', value: '1', checked: true, onClass: 'cs' },
    { label: '3+', value: '3', checked: false, onClass: 'cs' },
    { label: '5+', value: '5', checked: false, onClass: 'cs' },
  ],
  stage: [
    { label: 'Early', value: 'Early', checked: true, onClass: 'cs' },
    { label: 'Mid', value: 'Mid', checked: true, onClass: 'cs' },
    { label: 'Late', value: 'Late', checked: true, onClass: 'cs' },
  ],
  vc_expertise: [
    { label: 'FT', value: 'FinTech', checked: false, onClass: 'ft' },
    { label: 'RE', value: 'Real Estate', checked: false, onClass: 're' },
    { label: 'CI', value: 'Cloud', checked: false, onClass: 'ci' },
    { label: 'CS', value: 'Security', checked: false, onClass: 'cs' },
    { label: 'AI', value: 'AI/ML', checked: false, onClass: 'ai' },
    { label: 'MT', value: 'MarTech', checked: false, onClass: 'mt' },
    { label: 'IT', value: 'Insurtech', checked: false, onClass: 'it' },
  ],
  timeframe: [
    { label: '90d', value: '', checked: true, onClass: 'cs' },
    { label: 'Q1', value: 'Q1', checked: false, onClass: 'cs' },
    { label: 'Q2', value: 'Q2', checked: false, onClass: 'cs' },
    { label: 'Q3', value: 'Q3', checked: false, onClass: 'cs' },
    { label: 'Q4', value: 'Q4', checked: false, onClass: 'cs' },
    { label: 'YTD', value: 'YTD', checked: false, onClass: 'cs' },
  ],
  round_type: [{ label: 'Show All Rounds', value: true, checked: false, onClass: 'cs' }],
  location: [
    {
      label: 'United States',
      value: 'United States',
      checked: true,
      onClass: 'cs',
    },
    {
      label: 'Canada',
      value: 'Canada',
      checked: false,
      onClass: 'cs',
    },
    {
      label: 'Mexico',
      value: 'Mexico',
      checked: false,
      onClass: 'cs',
    },
    {
      label: 'Israel',
      value: 'Israel',
      checked: false,
      onClass: 'cs',
    },
    {
      label: 'Other',
      value: 'Other',
      checked: false,
      onClass: 'cs',
    },
    {
      label: 'Global (All)',
      value: 'Global (All)',
      checked: false,
      onClass: 'cs',
    },
  ],
};

const getVerticalPerUser = (userEmail) => {
  switch (userEmail) {
    case 'don@thomvest.com':
      return 'FinTech';
    case 'dave@thomvest.com':
      return 'FinTech';
    case 'ashish@thomvest.com':
      return 'Cloud';
    case 'alex@thomvest.com':
      return 'Real Estate';
    case 'colin@thomvest.com':
      return 'Real Estate';
    case 'aaron@thomvest.com':
      return 'Cloud';
    default:
      return 'FinTech';
  }
};

const INITIAL_STATE = {
  searchFilters: {
    verticals: [],
    stage: [],
    vc_score: [],
    status: [],
    location: {},
    subverticals: [],
  },
  siteFilters: SITE_FILTERS,
};

const getActiveFilters = (filterList) => filterList.filter((f) => f.checked).map((f) => f.value);

const getInitialState = (params, userEmail) => {
  const initialState = JSON.parse(JSON.stringify(INITIAL_STATE));
  const { siteFilters, searchFilters } = initialState;
  const vertical = getVerticalPerUser(userEmail);

  // Uncheck all verticals first to prevent defaults from interfering
  siteFilters.verticals.forEach((v) => {
    v.checked = false;
  });

  // Set default vertical only if no verticals are specified in params
  if (!params.verticals || params.verticals.length === 0) {
    siteFilters.verticals.forEach((v) => {
      v.checked = v.label === vertical || v.value === vertical;
    });
  }

  // If no params at all, return early with default vertical set
  if (Object.keys(params).length === 0) {
    return initialState;
  }

  for (const [filterList, values] of Object.entries(params)) {
    const filters = siteFilters[filterList === 'country' ? 'location' : filterList];

    if (!filters) continue;

    for (let i = 0; i < filters.length; i += 1) {
      if (filterList === 'location') {
        filters[i].checked = values.country === filters[i].value;
      } else if (values.includes(filters[i].value)) {
        filters[i].checked = true;
      }
    }

    searchFilters[filterList] = getActiveFilters(filters);
  }

  // Uncheck "90d" if any other timeframe is selected
  if (params.timeframe?.length && siteFilters.timeframe.some((f) => f.checked && f.value)) {
    siteFilters.timeframe.find((f) => f.label === '90d').checked = false;
  }

  if (params.location) {
    const { city, region, country } = params.location;

    if (city || region || country) {
      searchFilters.location = {
        ...(city && { city }),
        ...(region && { region }),
        ...(country && { country }),
      };
    }
  }

  return initialState;
};

const reducer = (state, action) => {
  if (!state) return null;

  if (action === 'reset') {
    return { ...JSON.parse(JSON.stringify(INITIAL_STATE)) };
  }

  const { siteFilters, searchFilters } = state;
  const { filterList, value } = action;

  const isMultiSelect = filterList === 'vc_expertise' || filterList === 'stage';

  if (isMultiSelect) {
    siteFilters[filterList].map((filter) => {
      if (filter.value === value) {
        filter.checked = !filter.checked;
      }
      return filter;
    });
  } else {
    siteFilters[filterList].map((filter) => {
      filter.checked = filter.value === value;
      return filter;
    });
  }

  if (filterList === 'location') {
    const newState = {
      ...state,
      searchFilters: {
        ...searchFilters,
        location: value,
      },
    };
    return newState;
  }

  searchFilters[filterList] = getActiveFilters(siteFilters[filterList]);

  const newState = { ...state };
  return newState;
};

export const AdvancedSearchV2Provider = ({ params, children }) => {
  const userEmail = getUserEmail();
  const initialState = getInitialState(params, userEmail);
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <AdvancedSearchV2Context.Provider value={[state, dispatch]}>
      {children}
    </AdvancedSearchV2Context.Provider>
  );
};
